import { useContext, type ComponentProps, type ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { clsx } from 'clsx'

import _ButtonInternal from './ButtonInternal'
import { rem } from 'polished'
import { LargeButtonTypography, MediumButtonTypography, SmallButtonTypography } from 'LuxKit/Typography/ButtonTypography'
import { ProductPaletteContext, type ProductPalette } from 'contexts/ProductPaletteContext'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

export type _BaseButtonKind = 'primary' | 'secondary' | 'tertiary'
export type _BaseButtonVariant = 'default' | 'ghost' | 'dark'
export type _BaseButtonVisualState = 'default' | 'hover' | 'active' | 'focus'
export type _BaseButtonShape = 'square' | 'round' | 'circle'
export type _BaseButtonSize = 'xsmall' | 'small' | 'medium' | 'large'
export type _BaseButtonOutdent = 'start' | 'end'
export type ButtonPalette = ProductPalette | 'inherit'

function resolveButtonPalette(
  palette: ButtonPalette | undefined,
  contextPalette: ProductPalette,
): ProductPalette {
  if (palette === 'inherit') return contextPalette
  if (palette) return palette
  if (contextPalette === 'lux-plus' || contextPalette === 'ultralux') {
    return contextPalette
  }
  return 'default'
}

const selectedStyles = (styles: ReturnType<typeof css>) => css`
  &:where(.selected),
  &:where(.is-selected),
  &:where([data-selected='true']),
  &:where([aria-selected='true']),
  &:where([data-state='selected']) {
    ${styles}
  }
`

const rebrandPaletteStyles = css`
  &:where(.product-palette-default),
  &:where(.product-palette-neutral) {
    --button-background: var(--palette-highlight-primary-dark-background);
    --button-foreground: var(--palette-highlight-primary-dark-foreground);
    --button-contrast: var(--palette-highlight-primary-dark-contrast);
  }

  &:where(.product-palette-lux-plus) {
    --button-background: var(--palette-product-lux-plus-background);
    --button-foreground: var(--palette-product-lux-plus-foreground);
    --button-contrast: var(--palette-product-lux-plus-contrast);
  }

  &:where(.product-palette-ultralux) {
    --button-background: var(--palette-product-ultralux-background);
    --button-foreground: var(--palette-product-ultralux-foreground);
    --button-contrast: var(--palette-product-ultralux-contrast);
  }

  &:where(.product-palette-tier-bronze) {
    --button-background: var(--palette-product-loyalty-bronze-dark-background);
    --button-foreground: var(--palette-product-loyalty-bronze-normal-background);
    --button-contrast: var(--palette-product-loyalty-bronze-dark-contrast);
  }

  &:where(.product-palette-tier-silver) {
    --button-background: var(--palette-product-loyalty-silver-dark-background);
    --button-foreground: var(--palette-product-loyalty-silver-normal-background);
    --button-contrast: var(--palette-product-loyalty-silver-dark-contrast);
  }

  &:where(.product-palette-tier-gold) {
    --button-background: var(--palette-product-loyalty-gold-dark-background);
    --button-foreground: var(--palette-product-loyalty-gold-normal-background);
    --button-contrast: var(--palette-product-loyalty-gold-dark-contrast);
  }

  &:where(.product-palette-tier-platinum) {
    --button-background: var(--palette-product-loyalty-platinum-dark-background);
    --button-foreground: var(--palette-product-loyalty-platinum-normal-background);
    --button-contrast: var(--palette-product-loyalty-platinum-dark-contrast);
  }

  &:where(.product-palette-tier-infinite) {
    --button-background: var(--palette-product-loyalty-infinite-dark-background);
    --button-foreground: var(--palette-product-loyalty-infinite-normal-background);
    --button-contrast: var(--palette-product-loyalty-infinite-dark-contrast);
  }
`

const rebrandBrandedStyles = css`
  &:where(.kind-primary) {
    &:where(.variant-default) {
      --button-internal-default-color: var(--button-contrast);
      --button-internal-default-background-color: var(--button-background);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--button-contrast);
      --button-internal-focus-background-color: var(--button-background);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--button-contrast);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--button-contrast);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--button-contrast);
      --button-internal-disabled-background-color: var(--button-background);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);
    }
    &:where(.variant-dark) {
      --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-default-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-focus-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-hover-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-active-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-disabled-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);
    }
    &:where(.variant-ghost) {
      --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-default-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-focus-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-hover-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-active-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-disabled-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);
    }
  }
  &:where(.kind-secondary) {
    &:where(.variant-default) {
      --button-internal-default-color: var(--button-foreground);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--button-foreground);

      --button-internal-focus-color: var(--button-foreground);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--button-foreground);

      --button-internal-hover-color: var(--button-foreground);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-foreground);

      --button-internal-active-color: var(--button-foreground);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-foreground);

      --button-internal-disabled-color: var(--button-foreground);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--button-foreground);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--button-contrast);
        --button-internal-default-background-color: var(--button-background);
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--button-contrast);
        --button-internal-focus-background-color: var(--button-background);
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--button-contrast);
        --button-internal-hover-background-color: color-mix(in srgb, var(--button-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--button-contrast);
        --button-internal-active-background-color: color-mix(in srgb, var(--button-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--button-contrast);
        --button-internal-disabled-background-color: var(--button-background);
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
    &:where(.variant-dark) {
      --button-internal-default-color: var(--palette-neutral-surface-static-white);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--palette-neutral-surface-static-white);

      --button-internal-focus-color: var(--palette-neutral-surface-static-white);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--palette-neutral-surface-static-white);

      --button-internal-hover-color: var(--palette-neutral-surface-static-white);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--palette-neutral-surface-static-white);

      --button-internal-active-color: var(--palette-neutral-surface-static-white);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--palette-neutral-surface-static-white);

      --button-internal-disabled-color: var(--palette-neutral-surface-static-white);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--palette-neutral-surface-static-white);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-default-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-focus-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-hover-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-active-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-disabled-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
    &:where(.variant-ghost) {
      --button-internal-default-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--palette-neutral-surface-normal-foreground);

      --button-internal-focus-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--palette-neutral-surface-normal-foreground);

      --button-internal-hover-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--palette-neutral-surface-normal-foreground);

      --button-internal-active-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--palette-neutral-surface-normal-foreground);

      --button-internal-disabled-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-default-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-focus-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-hover-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-active-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-disabled-background-color: var(--palette-neutral-surface-normal-background);
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
  }
  &:where(.kind-tertiary) {
    &:where(.variant-default) {
      --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-default-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-focus-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--palette-neutral-surface-normal-contrast);
        --button-internal-disabled-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
    &:where(.variant-dark) {
      --button-internal-default-color: var(--palette-neutral-surface-static-white);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--palette-neutral-surface-static-white);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--palette-neutral-surface-static-white);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--palette-neutral-surface-static-white);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--palette-neutral-surface-static-white);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--palette-neutral-surface-static-white);
        --button-internal-default-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--palette-neutral-surface-static-white);
        --button-internal-focus-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--palette-neutral-surface-static-white);
        --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--palette-neutral-surface-static-white);
        --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--palette-neutral-surface-static-white);
        --button-internal-disabled-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
    &:where(.variant-ghost) {
      --button-internal-default-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-default-background-color: var(--button-transparent);
      --button-internal-default-border-color: var(--button-transparent);

      --button-internal-focus-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-focus-background-color: var(--button-transparent);
      --button-internal-focus-border-color: var(--button-transparent);

      --button-internal-hover-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);

      --button-internal-active-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);

      --button-internal-disabled-color: var(--palette-neutral-surface-normal-foreground);
      --button-internal-disabled-background-color: var(--button-transparent);
      --button-internal-disabled-border-color: var(--button-transparent);
      --button-internal-disabled-opacity: var(--button-disabled-opacity);

      ${selectedStyles(css`
        --button-internal-default-color: var(--palette-neutral-surface-normal-foreground);
        --button-internal-default-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
        --button-internal-default-border-color: var(--button-transparent);

        --button-internal-focus-color: var(--palette-neutral-surface-normal-foreground);
        --button-internal-focus-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
        --button-internal-focus-border-color: var(--button-transparent);

        --button-internal-hover-color: var(--palette-neutral-surface-normal-foreground);
        --button-internal-hover-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
        --button-internal-hover-border-color: var(--button-transparent);

        --button-internal-active-color: var(--palette-neutral-surface-normal-foreground);
        --button-internal-active-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
        --button-internal-active-border-color: var(--button-transparent);

        --button-internal-disabled-color: var(--palette-neutral-surface-normal-foreground);
        --button-internal-disabled-background-color: color-mix(in srgb, var(--button-transparent), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-16));
        --button-internal-disabled-border-color: var(--button-transparent);
        --button-internal-disabled-opacity: var(--button-disabled-opacity);
      `)}
    }
  }
`

const legacyBrandedStyles = css`
  /* ButtonInternal consumes these state variables; the values below preserve legacy colour decisions. */
  --button-internal-default-color: var(--button-color);
  --button-internal-default-background-color: var(--button-background-color);
  --button-internal-default-border-color: var(--button-border-color, transparent);
  --button-internal-hover-color: var(--button-hover-color);
  --button-internal-hover-background-color: var(--button-hover-background-color);
  --button-internal-hover-border-color: var(--button-hover-border-color, transparent);
  --button-internal-active-color: var(--button-active-color);
  --button-internal-active-background-color: var(--button-active-background-color);
  --button-internal-active-border-color: var(--button-active-border-color, transparent);
  --button-internal-focus-color: var(--button-focus-color);
  --button-internal-focus-background-color: var(--button-focus-background-color);
  --button-internal-focus-border-color: var(--button-focus-border-color, transparent);
  --button-internal-focus-ring-color: var(--button-focus-ring-color);
  --button-internal-disabled-color: var(--button-disabled-color);
  --button-internal-disabled-background-color: var(--button-disabled-background-color);
  --button-internal-disabled-border-color: var(--button-disabled-border-color, transparent);

  --color-primary-bg: ${props => props.theme.palette.brand.primary.normal};
  --color-primary-fg: ${props => props.theme.palette.neutral.surface.darkContrast};
  --color-primary-hover: ${props => props.theme.palette.brand.primary.hover};
  --color-primary-active: ${props => props.theme.palette.brand.primary.active};

  &:where(.kind-primary) {
    /* Common colour settings across all variants */
    --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};

    &:where(.product-palette-ultralux) {
      --color-primary-bg: ${props => props.theme.palette.product.ultralux.background};
      --color-primary-fg: ${props => props.theme.palette.product.ultralux.contrast};
      --color-primary-hover: ${props => props.theme.palette.product.ultralux.hover};
      --color-primary-active: ${props => props.theme.palette.product.ultralux.active};
    }

    &:where(.product-palette-lux-plus) {
      --color-primary-bg: ${props => props.theme.palette.product.luxPlus.background};
      --color-primary-fg: ${props => props.theme.palette.product.luxPlus.contrast};
      --color-primary-hover: ${props => props.theme.palette.product.luxPlus.hover};
      --color-primary-active: ${props => props.theme.palette.product.luxPlus.active};
    }

    &:where(.variant-default), &:where(.variant-dark) {
      --button-color: var(--color-primary-fg);
      --button-background-color: var(--color-primary-bg);
      --button-border-color: var(--color-primary-bg);

      --button-disabled-color: var(--color-primary-fg);
      --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
      --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-hover-color: var(--color-primary-fg);
      --button-hover-background-color: var(--color-primary-hover);
      --button-hover-border-color: var(--color-primary-hover);

      --button-active-color: var(--color-primary-fg);
      --button-active-background-color: var(--color-primary-active);
      --button-active-border-color: var(--color-primary-active);

      --button-focus-color: var(--color-primary-fg);
      --button-focus-border-color: var(--color-primary-bg);
      --button-focus-background-color: var(--color-primary-bg);

      &:where(.is-selected) {
        /* This variant looks the same when selected, often the content will change instead */
      }
    }

    &:where(.variant-ghost) {
      --button-color: var(--color-primary-bg);
      --button-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
      --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-hover-color: var(--color-primary-hover);
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      --button-hover-border-color: ${props => props.theme.palette.neutral.surface.lightBackground};

      --button-active-color: var(--color-primary-active);
      --button-active-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-active-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      --button-focus-color: var(--color-primary-bg);
      --button-focus-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      &:where(.is-selected) {
        --button-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-background-color: var(--color-primary-bg);
        --button-border-color: var(--color-primary-bg);

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.darkForeground};

        --button-hover-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-hover-background-color: var(--color-primary-hover);
        --button-hover-border-color: var(--color-primary-hover);

        --button-active-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-active-background-color: var(--color-primary-active);
        --button-active-border-color: var(--color-primary-active);

        --button-focus-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-focus-background-color: var(--color-primary-bg);
        --button-focus-border-color: var(--color-primary-bg);

      }
    }
  }

  &:where(.kind-secondary) {
    &:where(.product-palette-lux-plus) {
      --color-primary-bg: ${props => props.theme.palette.product.luxPlus.altForeground};
      --color-primary-hover: ${props => props.theme.palette.product.luxPlus.altHover};
      --color-primary-active: ${props => props.theme.palette.product.luxPlus.altActive};
    }

    &:where(.product-palette-ultralux) {
      --color-primary-bg: ${props => props.theme.palette.product.ultralux.foreground};
      --color-primary-hover: ${props => props.theme.palette.product.ultralux.hover};
      --color-primary-active: ${props => props.theme.palette.product.ultralux.active};
    }

    &:where(.product-palette-tier-platinum) {
      --color-primary-bg: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
      --color-primary-fg: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
      --color-primary-hover: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
      --color-primary-active: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
    }

    &:where(.product-palette-tier-infinite) {
      --color-primary-bg: ${props => props.theme.palette.product.loyalty.infinite.darkBackground};
      --color-primary-fg: ${props => props.theme.palette.product.loyalty.infinite.darkContrast};
      --color-primary-hover: ${props => props.theme.palette.product.loyalty.infinite.normalBackground};
      --color-primary-active: ${props => props.theme.palette.product.loyalty.infinite.lightBackground};
    }

    &:where(.variant-default) {
      --button-color: var(--color-primary-bg);
      --button-background-color: transparent;
      --button-border-color: var(--color-primary-bg);

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-hover-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-hover-background-color: var(--color-primary-bg);
      --button-hover-border-color: var(--color-primary-bg);

      --button-active-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-active-background-color: var(--color-primary-bg);
      --button-active-border-color: var(--color-primary-bg );

      --button-focus-color: var(--color-primary-bg);
      --button-focus-border-color: var(--color-primary-bg);
      --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      &:where(.is-selected) {
        --button-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-background-color: var(--color-primary-bg);
        --button-border-color: var(--color-primary-bg);

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
        --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

        --button-hover-color: var(--color-primary-bg);
        --button-hover-background-color: transparent;
        --button-hover-border-color: var(--color-primary-bg);

        --button-focus-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-focus-background-color: var(--color-primary-bg);
        --button-focus-border-color: var(--color-primary-bg);

        --button-active-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-active-background-color: var(--color-primary-active);
        --button-active-border-color: var(--color-primary-active);
      }
    }

    &:where(.variant-ghost) {
      --button-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-background-color: transparent;
      --button-border-color: ${props => props.theme.palette.neutral.surface.darkForeground};

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-hover-color: var(--color-primary-bg);
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-hover-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      --button-active-color: var(--color-primary-bg);
      --button-active-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-active-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      --button-focus-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-focus-border-color: ${props => props.theme.palette.neutral.surface.darkForeground};
      --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.darkForeground};

      &:where(.is-selected) {
        --button-color: var(--color-primary-bg);
        --button-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
        --button-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForeground};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
        --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

        --button-hover-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-hover-background-color: transparent;
        --button-hover-border-color: ${props => props.theme.palette.neutral.surface.darkForeground};

        --button-active-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-active-background-color: transparent;
        --button-active-border-color: ${props => props.theme.palette.neutral.surface.darkForeground};

        --button-focus-color: var(--color-primary-bg);
        --button-focus-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
        --button-focus-border-color: ${props => props.theme.palette.neutral.surface.normalBackground};
        --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      }
    }

    &:where(.variant-dark) {
      --button-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      --button-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

      --button-hover-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      --button-hover-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-active-color: ${props => props.theme.palette.neutral.surface.lightContrast};
      --button-active-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      --button-active-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      --button-focus-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      --button-focus-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-focus-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};

      &:where(.is-selected) {
        --button-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
        --button-border-color: ${props => props.theme.palette.neutral.surface.darkBackground};

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
        --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};

        --button-hover-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
        --button-hover-border-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};

        --button-focus-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-focus-background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
        --button-focus-border-color: ${props => props.theme.palette.neutral.surface.darkBackground};
        --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};

        --button-active-color: ${props => props.theme.palette.neutral.surface.darkContrast};
        --button-active-background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
        --button-active-border-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      }
    }
  }

  &:where(.kind-tertiary) {
    /* Common colour settings across all variants */
    --button-focus-ring-color: ${props => props.theme.palette.neutral.surface.lightForeground};

    &:where(.variant-default) {
      --button-color: ${props => props.theme.palette.brand.primary.normal};
      --button-background-color: transparent;
      --button-border-color: transparent;

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

      --button-hover-color: ${props => props.theme.palette.brand.primary.hover};
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

      --button-active-color: ${props => props.theme.palette.brand.primary.active};

      --button-focus-color: ${props => props.theme.palette.brand.primary.normal};

      &:where(.is-selected) {
        --button-color: ${props => props.theme.palette.brand.primary.normal};
        --button-background-color: ${props => props.theme.palette.brand.primary.lightest};

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForeground};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};

        --button-hover-color: ${props => props.theme.palette.brand.primary.hover};
        --button-hover-background-color: ${props => props.theme.palette.brand.primary.lightest};

        --button-active-color: ${props => props.theme.palette.brand.primary.active};
        --button-active-background-color: ${props => props.theme.palette.brand.primary.lightest};

        --button-focus-color: ${props => props.theme.palette.brand.primary.normal};
        --button-focus-background-color: ${props => props.theme.palette.brand.primary.lightest};
      }
    }

    &:where(.variant-ghost) {
      --button-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-background-color: transparent;
      --button-border-color: transparent;

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

      --button-hover-color: ${props => props.theme.palette.neutral.surface.darkContrast};
      --button-hover-background-color: ${props => props.theme.palette.neutral.default.clear};

      --button-active-color: ${props => props.theme.palette.neutral.surface.darkForeground};

      --button-focus-color: ${props => props.theme.palette.neutral.surface.darkContrast};

      &:where(.is-selected) {
        --button-background-color: ${props => props.theme.palette.neutral.default.clear};

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.default.clear};

        --button-hover-color: ${props => props.theme.palette.neutral.surface.darkForeground};
        --button-hover-background-color: ${props => props.theme.palette.neutral.default.clear};

        --button-focus-background-color: ${props => props.theme.palette.neutral.default.clear};

        --button-active-color: ${props => props.theme.palette.neutral.surface.darkForeground};
        --button-active-background-color: ${props => props.theme.palette.neutral.default.clear};
      }
    }

    &:where(.variant-dark) {
      --button-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      --button-background-color: transparent;
      --button-border-color: transparent;

      --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

      --button-hover-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

      --button-active-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

      --button-focus-color: ${props => props.theme.palette.neutral.surface.normalContrast};

      &:where(.is-selected) {
        --button-color: ${props => props.theme.palette.neutral.surface.lightContrast};
        --button-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

        --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
        --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

        --button-hover-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
        --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

        --button-active-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
        --button-active-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

        --button-focus-color: ${props => props.theme.palette.neutral.surface.lightContrast};
        --button-focus-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      }
    }
  }
`

const shapeStyles = css`
  &:where(.shape-square) {
    --button-border-radius: var(--border-radius-s);
  }
  &:where(.shape-round, .shape-circle) {
    --button-border-radius: var(--border-radius-round);
  }
`

const sizeStyles = css`
  &:where(.size-small, .size-xsmall) {
    --button-height: ${rem(32)};
    --button-padding-x: ${rem(8)};

    ${SmallButtonTypography}
  }

  &:where(.size-medium) {
    --button-height: ${rem(40)};
    --button-padding-x: ${rem(12)};

    ${MediumButtonTypography}
  }

  &:where(.size-large) {
    --button-height: ${rem(48)};
    --button-padding-x: ${rem(16)};

    ${LargeButtonTypography}
  }
`

const outdentStyles = css`
  --button-outdent-inline-amount: var(--button-padding-x, 0);
  --button-outdent-block-amount: var(--button-padding-y, 0);
  --button-outdent-rtl-multiplier: 1;

  &:where(.has-horizontal-outdent-start) {
    --button-horizontal-outdent-multiplier: -1;
  }

  &:where(.has-horizontal-outdent-end) {
    --button-horizontal-outdent-multiplier: 1;
  }

  &:where(.has-vertical-outdent-start) {
    --button-vertical-outdent-multiplier: -1;
  }

  &:where(.has-vertical-outdent-end) {
    --button-vertical-outdent-multiplier: 1;
  }

  &:where(.is-outdented) {
    transform: translate(
      calc(
        var(--button-outdent-inline-amount) *
        var(--button-horizontal-outdent-multiplier, 0) *
        var(--button-outdent-rtl-multiplier)
      ),
      calc(
        var(--button-outdent-block-amount) *
        var(--button-vertical-outdent-multiplier, 0)
      )
    );
  }

  &:where(:dir(rtl)) {
    --button-outdent-rtl-multiplier: -1;
  }
`

/**
 * This is the 'base' button that other buttons should compose
 * This is effectively an 'abstract' component and should never be used on it's own
 * but comes with its own opinionated styles
 */
const StyledButtonInternal = styled(_ButtonInternal)`
  height: var(--button-height, auto);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: var(--button-border-radius, 0);
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  background-image: none;
  font-family: ${props => props.theme.font.secondary.family};
  font-weight: ${props => props.theme.font.secondary.weight.regular};
  white-space: nowrap;
  text-decoration: none;
  padding: var(--button-padding-y, 0) var(--button-padding-x, 0);
  flex-shrink: 0;

  ${sizeStyles}
  ${shapeStyles}
  ${outdentStyles}

  :where(body:not(.${REBRAND_PALETTE_CLASS})) & {
    ${legacyBrandedStyles}
  }

  :where(body.${REBRAND_PALETTE_CLASS}) & {
    /* Use transparent white instead of transparent, because transparent is rgba(0,0,0,0) and skews color-mix(in srgb, …) toward black. */
    --button-transparent: rgba(255, 255, 255, 0);
    --button-disabled-opacity: 18%;

    /* Some logical defaults, these should be overridden by a LuxKit button component. */
    --button-background: var(--palette-neutral-surface-dark-background);
    --button-foreground: var(--palette-neutral-surface-dark-foreground);
    --button-contrast: var(--palette-neutral-surface-dark-contrast);

    ${rebrandBrandedStyles}
    ${rebrandPaletteStyles}
  }
`

interface Props extends ComponentProps<typeof _ButtonInternal>, Utils.PropsWithDataAttributes {
  children?: ReactNode
  kind: _BaseButtonKind
  variant?: _BaseButtonVariant
  shape?: _BaseButtonShape
  size?: _BaseButtonSize
  horizontalOutdent?: _BaseButtonOutdent
  verticalOutdent?: _BaseButtonOutdent
  isSelected?: boolean
  /**
   * Button colour palette. Omit to use the brand default, unless the nearest
   * `ProductPaletteProvider` is `lux-plus` or `ultralux`, which are inherited.
   * Pass `"inherit"` to follow any provider palette, including loyalty tiers.
   * Pass an explicit palette to override both the default and the provider.
   */
  palette?: ButtonPalette
}

function BaseButton({
  children,
  className,
  kind,
  variant = 'default',
  shape = 'square',
  size = 'medium',
  palette,
  horizontalOutdent,
  isSelected,
  verticalOutdent,
  ...rest
}: Props) {
  const contextPalette = useContext(ProductPaletteContext)
  const resolvedPalette = resolveButtonPalette(palette, contextPalette)

  return <StyledButtonInternal
    {...rest}
    className={clsx(
      className,
      `kind-${kind}`,
      `variant-${variant}`,
      `shape-${shape}`,
      `size-${size}`,
      `product-palette-${resolvedPalette}`,
      {
        'is-selected': isSelected,
        'is-outdented': horizontalOutdent || verticalOutdent,
        'has-horizontal-outdent-start': horizontalOutdent === 'start',
        'has-horizontal-outdent-end': horizontalOutdent === 'end',
        'has-vertical-outdent-start': verticalOutdent === 'start',
        'has-vertical-outdent-end': verticalOutdent === 'end',
      },
    )}
  >
    {children}
  </StyledButtonInternal>
}

export default BaseButton
